import type { LanguageType } from '@/types/language';

/**
 * ### IndustrialCopyByLocale
 *
 * One piece of Listen AI Industrial page copy, written once per site
 * language. Each language is phrased independently rather than translated
 * word for word, so the terms readers of that language actually search for
 * survive; the figures inside a sentence stay identical across languages.
 */
export type IndustrialCopyByLocale = Record<LanguageType, string>;

/**
 * ### INDUSTRIAL_PAGE_TITLE
 *
 * Document and Open Graph title of the Listen AI Industrial page. Carries the
 * broad search terms the press kit assigns to this page; the narrower terms
 * live in {@link INDUSTRIAL_PAGE_HEADING} and
 * {@link INDUSTRIAL_PAGE_DESCRIPTION} so no single string has to hold all of
 * them at the cost of reading like a keyword list.
 */
export const INDUSTRIAL_PAGE_TITLE: IndustrialCopyByLocale = {
  ko: '소리 AI 품질검사·예지보전 | Listen AI Industrial',
  en: 'Sound AI Quality Inspection and Predictive Maintenance | Listen AI Industrial',
};

/**
 * ### INDUSTRIAL_PAGE_HEADING
 *
 * The page's single h1, rendered for assistive technology and crawlers rather
 * than shown in the layout. Spells out the narrow inspection terms the title
 * has no room for, so the page states its full subject once in a heading
 * element instead of only in metadata.
 */
export const INDUSTRIAL_PAGE_HEADING: IndustrialCopyByLocale = {
  ko: '기계 이상음 분석에 기반한 소리 AI 품질검사와 예지보전, 모터 품질 검사와 커넥터 체결음부터 설비 이상 진단까지',
  en: 'Sound AI quality inspection and predictive maintenance built on machine anomaly sound analysis, from motor quality inspection and connector engagement sound to equipment fault diagnosis',
};

/**
 * ### INDUSTRIAL_PAGE_DESCRIPTION
 *
 * Meta description of the Listen AI Industrial page, read by the route SEO
 * defaults. Repeats the page's search terms in one sentence a search result
 * can show whole.
 *
 * @see {@link INDUSTRIAL_PAGE_TITLE} The title this description sits under
 */
export const INDUSTRIAL_PAGE_DESCRIPTION: IndustrialCopyByLocale = {
  ko: 'Listen AI Industrial은 모터 품질 검사와 커넥터 체결음 검사부터 설비 이상 진단까지, 기계 이상음 분석 기반 소리 AI로 양산 라인 품질검사와 예지보전을 지원합니다.',
  en: 'Listen AI Industrial supports production-line quality inspection and predictive maintenance with sound AI built on machine anomaly sound analysis, from motor quality inspection and connector engagement sound to equipment fault diagnosis.',
};

/**
 * ### IndustrialUseCaseImage
 *
 * Illustration of one use case at the two widths the section renders. Both
 * files are the artwork of the inspection card this use case replaced, so a
 * use case without a predecessor has no entry here.
 */
export type IndustrialUseCaseImage = {
  /**
   * ### pc
   *
   * Artwork used from the `lg` breakpoint up.
   */
  pc: string;

  /**
   * ### mobile
   *
   * Artwork used below the `lg` breakpoint.
   */
  mobile: string;
};

/**
 * ### IndustrialUseCaseGroup
 *
 * One of the three production use cases the press kit groups Listen AI
 * Industrial into. Titles and descriptions are press-kit copy reproduced
 * verbatim: every figure in them is a public performance claim scoped to that
 * one use case, so changing a word here needs the review press material gets.
 *
 * Korean is the written form; the English rendering lives in the locale
 * dictionary under the Korean string as its key.
 */
export type IndustrialUseCaseGroup = {
  /**
   * ### title
   *
   * Name of the use-case group as the press kit names it.
   */
  title: string;

  /**
   * ### description
   *
   * The group's press-kit paragraph, carrying its accuracy or coverage figure
   * together with the condition that figure was measured under.
   */
  description: string;

  /**
   * ### tags
   *
   * Inspection keywords inherited from the card this group replaced. They
   * describe the work, never the performance, so they carry no figures.
   */
  tags: ReadonlyArray<string>;

  /**
   * ### image
   *
   * Artwork inherited from the card this group replaced.
   */
  image: IndustrialUseCaseImage;

  /**
   * ### focusAreas
   *
   * Sound types the press kit singles out for this group. Only the predictive
   * maintenance group names any; the other two are left undefined rather than
   * given an empty list, because the press kit states no focus for them.
   */
  focusAreas?: ReadonlyArray<string>;
};

/**
 * ### INDUSTRIAL_USE_CASE_GROUPS
 *
 * The three production use cases in the order the press kit leads with them:
 * inspection on the line, inspection at the end of the line, then monitoring
 * of the line's own equipment. Reordering changes what a reader meets first
 * and is an editorial decision, not a layout one.
 *
 * @see {@link INDUSTRIAL_PRODUCTION_METRICS} The figures these groups are measured by
 */
export const INDUSTRIAL_USE_CASE_GROUPS: ReadonlyArray<IndustrialUseCaseGroup> =
  [
    {
      title: '커넥터 체결 검사',
      description:
        'OEM 조립 라인의 고소음을 극복하고, 커넥터 및 볼트의 연속 체결 작업을 99%+ 정확도로 실시간 감지합니다',
      tags: ['커넥터체결', '체결음검사', '조립라인', 'IPQC', '체결불량'],
      image: {
        pc: '/images/apps/solution/industrial/sec_01_img_02.png',
        mobile: '/images/apps/solution/industrial/sec_01_img_02_m.png',
      },
    },
    {
      title: '회전체 이음 검사',
      description:
        '비전 센서가 놓치는 모터·액추에이터의 내부 결함까지, EOL 단계에서 24/7 전수 검사해 불량 유출을 99.8% 차단합니다',
      tags: ['회전체검사', '모터', '액추에이터', '베어링', '이음진단'],
      image: {
        pc: '/images/apps/solution/industrial/sec_01_img_01.png',
        mobile: '/images/apps/solution/industrial/sec_01_img_01_m.png',
      },
    },
    {
      title: '설비 모니터링 예지보전(PdM)',
      description:
        '15TB 규모의 제조 현장 음향 데이터셋과 70가지 이상의 소리 유형 분석 경험을 기반으로, 설비 이상 신호를 사전에 감지해 다운타임을 사전에 차단합니다. 멈춤 없는 공장을 만드는 소리 기반 예지보전(PdM)입니다',
      tags: [
        '노후화진단',
        '베어링',
        '설비진단',
        '예지보전',
        'Predictive Maintenance',
      ],
      image: {
        pc: '/images/apps/solution/industrial/sec_01_img_03.png',
        mobile: '/images/apps/solution/industrial/sec_01_img_03_m.png',
      },
      focusAreas: ['베어링', '가스 유출음'],
    },
  ];

/**
 * ### IndustrialProductionMetric
 *
 * One headline figure with the measurement it belongs to. The scope travels
 * with the value because Industrial figures are measured per inspection type:
 * quoting one without its scope turns a scoped result into a blanket claim.
 */
export type IndustrialProductionMetric = {
  /**
   * ### value
   *
   * The figure as the press kit writes it, including its `%` or `+`. Not run
   * through the locale dictionary, because the digits do not translate.
   */
  value: string;

  /**
   * ### label
   *
   * What the figure measures, named narrowly enough that it cannot be read as
   * covering another inspection type.
   */
  label: string;

  /**
   * ### scope
   *
   * The conditions the figure was measured under. A figure whose scope the
   * press kit does not state does not belong in this list at all.
   */
  scope: string;
};

/**
 * ### INDUSTRIAL_PRODUCTION_METRICS
 *
 * The three production figures the press kit publishes for Listen AI
 * Industrial, in the order of {@link INDUSTRIAL_USE_CASE_GROUPS}.
 *
 * These are deliberately not the figures quoted in the FAQ. The FAQ carries
 * 99.87% and 99.78%, which are press-release results tied to a named customer
 * and a named release date; the values here are the press kit's own scoped
 * claims. The two sets are never mixed inside one sentence, because their
 * measurement scopes differ.
 *
 * @see {@link INDUSTRIAL_FAQ_BY_LOCALE} The separately sourced press-release figures
 */
export const INDUSTRIAL_PRODUCTION_METRICS: ReadonlyArray<IndustrialProductionMetric> =
  [
    {
      value: '99%+',
      label: '커넥터·볼트 연속 체결 실시간 감지 정확도',
      scope: 'OEM 조립 라인의 고소음 환경 기준',
    },
    {
      value: '99.8%',
      label: 'EOL 불량 유출 차단율',
      scope: 'EOL 단계 모터·액추에이터 24/7 전수 검사 기준',
    },
    {
      value: '15TB',
      label: '제조 현장 음향 데이터셋',
      scope: '설비 모니터링 예지보전(PdM) 문맥 기준, 소리 유형 70가지 이상',
    },
  ];

/**
 * ### IndustrialFasteningPoint
 *
 * One fastening defect the connector engagement inspection is bought to
 * catch, with the sound cue that separates it from a sound engagement.
 */
export type IndustrialFasteningPoint = {
  /**
   * ### title
   *
   * The defect as a line operator would name it, not in product vocabulary.
   */
  title: string;

  /**
   * ### description
   *
   * How the inspection tells that defect from a full engagement.
   */
  description: string;

  /**
   * ### tags
   *
   * Inspection keywords describing the work, never its performance.
   */
  tags: ReadonlyArray<string>;
};

/**
 * ### INDUSTRIAL_FASTENING_POINTS
 *
 * The connector engagement deep dive that follows the use-case section: what
 * the inspection catches, one defect at a time.
 *
 * The entries are ordered the way a reader qualifies the inspection — the
 * defect they already know they have, the one they cannot see, then whether
 * any of it survives their shop-floor noise.
 *
 * @see {@link INDUSTRIAL_USE_CASE_GROUPS} The section this one deepens
 */
export const INDUSTRIAL_FASTENING_POINTS: ReadonlyArray<IndustrialFasteningPoint> =
  [
    {
      title: '덜 잠긴 설체결',
      description:
        '1차 락에서 멈춘 설체결과 소프트 커넥션은 2차 락 체결음이 나지 않습니다. 1차 락 3~16kHz, 2차 락 7~16kHz 대역을 나눠 분석해 완전 체결과 구분합니다',
      tags: ['설체결', '소프트커넥션', '주파수대역분석'],
    },
    {
      title: '빠진 체결',
      description:
        '체결음의 종류와 순서는 구분하지 않고, 한 사이클에서 감지한 체결음 수를 목표 수와 비교합니다. 목표에 못 미치면 작업이 끝나기 전에 NG로 알립니다',
      tags: ['체결수량', '사이클판정', '전수검사'],
    },
    {
      title: '소음에 묻힌 체결음',
      description:
        '임팩트 렌치, 에어건, 금속 마찰음, 컨베이어 소음이 겹치는 85dB 이상 환경을 전제로 학습해, 작업자가 귀와 손끝 감각으로 판단하던 체결음만 분리합니다',
      tags: ['고소음환경', '체결음분리', '실시간분석'],
    },
  ];

/**
 * ### INDUSTRIAL_FASTENING_METRICS
 *
 * The connector engagement figures quoted from the 11 June 2026 press
 * release, in the order of {@link INDUSTRIAL_FASTENING_POINTS}.
 *
 * These are the press-release results tied to a named customer and release
 * date, not the press kit's own scoped claims in
 * {@link INDUSTRIAL_PRODUCTION_METRICS}. The two sets sit in separate sections
 * and are never merged into one value, because their measurement scopes
 * differ; every entry here therefore names its source in its own scope line.
 *
 * @see {@link INDUSTRIAL_PRODUCTION_METRICS} The press kit's own scoped figures
 */
export const INDUSTRIAL_FASTENING_METRICS: ReadonlyArray<IndustrialProductionMetric> =
  [
    {
      value: '99.87%',
      label: '커넥터 체결음 검사 정확도',
      scope:
        '글로벌 완성차 H사 한국·멕시코 생산 라인 적용 결과, 2026년 6월 11일 보도자료 기준',
    },
    {
      value: '1초 내외',
      label: '제품당 체결 검사 시간',
      scope: '인라인 전수검사 기준, 라인 Cycle Time 충족 여부는 PoC에서 확인',
    },
    {
      value: '85dB 이상',
      label: '체결 검사가 가능한 현장 소음',
      scope: '임팩트 렌치·에어건·금속 마찰음·컨베이어 소음이 겹치는 환경 기준',
    },
  ];
